import { PropType } from 'vue';
import plantsClient from '@/core/services/plants';
import { Options, Vue } from 'vue-class-component';
import {
  BuildingDTO,
  ApartmentDTO,
  BuildingCreateDTO,
  EntrancePlantDetailDTO,
  AddressingType,
  FloorDTO,
  RiserDTO,
  RiserInterfaceDTO,
  RiserInterfaceMode,
  PeInterfaceConfig,
  EntrancePlantStructureCreateDTO,
  PePlantConfigDTO,
  PePlantConfig,
} from '@/../client-generator/generated-clients/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import NumberButtons from '@/views/components/NumberButtons/NumberButtons.vue';
import { mapState } from 'vuex';
import { toLabel } from '@/core/utils/EntrancePlant/denomination-helper';
import { getInterfaceModeLabel, noAppartmentOptions, EMPTY_GUID } from '@/core/utils/EntrancePlant/entrance-plant.utils';

@Options({
  name: 'DialogAddEditApartment',
  props: {
    building: {
      type: Object as PropType<BuildingDTO>,
      required: true,
    },
    riser: {
      type: Object as PropType<RiserDTO>,
      required: false,
    },
    isVisible: {
      type: Boolean,
      required: true,
    },
    apartmentToEdit: {
      type: Object as PropType<ApartmentDTO>,
      required: false,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant']),
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (this.isDialogVisible) {
        this.invalidNameReason = '';
        this.isValidName = true;
        this.initApartment();
      }
    },
    'apartment.name': function () {
      if (this.apartment) {
        this.verifyValidityOfName();
      }
    },
    'apartment.riserId': function () {
      if(this.isBrfa && !this.currentEntrancePlant.hasRiserInterface) {
        this.loadRisersFloorApartment();
      }
    },
    'selectedInterfaceId': function() {
      this.apartment.riserInterfaceId = this.selectedInterfaceId;
    }
  },
  components: {
    NumberButtons,
  },
  emits: ['hide-dialog', 'update-list'],
})
export default class DialogAddEditApartment extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;
  building!: BuildingDTO;
  riser!: RiserDTO;
  apartmentToEdit!: ApartmentDTO;
  currentRiserInterfaces: PeInterfaceConfig[] = [];
  apartment: ApartmentDTO = new ApartmentDTO();

  newApartments = 0;

  isVisible!: boolean;
  isDialogVisible = false;
  isEdit = false;
  riserFloors: { floor: FloorDTO; apartments: ApartmentDTO[] }[] = [];
  isBrfa = false;

  // Naming validation props
  isValidName = true;
  invalidNameReason: string = '';

  selectedInterfaceId: string = '';

  get isBackboneContext() {
    return this.currentEntrancePlant.hasRiserInterface && (!this.riser?.riserId || this.riser.riserId === EMPTY_GUID);
  }

  // BRFA mode: track apartments per floor per interface
  floorsInterfaceConfig: Array<{
    floor: FloorDTO;
    interfaceApartments: Array<{
      interface: RiserInterfaceDTO;
      numberOfApartments: number;
    }>;
  }> = [];

  get availableApartments() {
    const totalPlantApartments = this.currentEntrancePlant.buildings?.reduce(
      (acc, building) => acc + building.apartments?.length!,
      0
    ) || 0;
    return 3999 - totalPlantApartments;
  }

  mounted() {
    this.isBrfa =
      this.currentEntrancePlant.addressingType ===
      AddressingType.BuildingRiserFloorApartment;
    this.isDialogVisible = this.isVisible;
  }

  noAppartmentOptions(riserInterface: PeInterfaceConfig | RiserInterfaceDTO): { label: string; value: number }[] {
    //ogni interfaccia max 100ap, da tenere conto degli appartamenti gia presetti.
    let alreadyPresentAp = 0;
    let interfaceId = '';
    
    if ('id' in riserInterface) {
      // PeInterfaceConfig (hasRiserInterface mode)
      interfaceId = (riserInterface as PeInterfaceConfig).id ?? '';
      alreadyPresentAp = this.building.apartments?.filter(ap => ap.riserInterfaceId === interfaceId).length ?? 0;
    } else {
      // RiserInterfaceDTO (BRFA mode)
      interfaceId = (riserInterface as RiserInterfaceDTO).riserInterfaceId ?? '';
      alreadyPresentAp = this.building.apartments?.filter(ap => ap.riserInterfaceId === interfaceId).length ?? 0;
    }
    
    const maxApp = Math.min(100 - alreadyPresentAp, this.availableApartments);
    const count = noAppartmentOptions(maxApp, true);

    return count;
  }

  async initApartment() {
    if (this.apartmentToEdit) {
      this.isEdit = true;
      this.apartment = new ApartmentDTO(this.apartmentToEdit);
      if (this.currentEntrancePlant.hasRiserInterface)
      {
        this.selectedInterfaceId = this.apartment.riserInterfaceId!;
      }
    } else {
      this.isEdit = false;
      if (this.isBrfa && !this.currentEntrancePlant.hasRiserInterface) {
        // Old BRFA logic (without hasRiserInterface)
        this.apartment = new ApartmentDTO();
        this.apartment.buildingId = this.building.buildingId;
        this.apartment.riserId = this.building.risers![0].riserId;
        this.loadRisersFloorApartment();
        this.apartment.floorId = this.riserFloors[0].floor.floorId;
        this.apartment.name = this.getNewApartmentName();
      }
      else if (this.isBrfa && this.currentEntrancePlant.hasRiserInterface) {
        // New BRFA with hasRiserInterface logic
        this.apartment = new ApartmentDTO();
        this.apartment.buildingId = this.building.buildingId;
        this.apartment.riserId = this.riser.riserId!;
        this.loadRisersFloorApartment();
      }
      if (this.currentEntrancePlant.hasRiserInterface && !this.isBrfa)
      {
        this.selectedInterfaceId = this.risersInterfaces[0].riserInterfaceId!;
        this.currentRiserInterfaces = this.riser.interfaces?.map(riserInterface => {
          return new PeInterfaceConfig({
            peBuildingConfigId: this.building.buildingId,
            peRiserConfigId: this.riser.riserId!,
            mode: riserInterface.mode!,
            numberOfApartments: 0,
            id: riserInterface.riserInterfaceId!
          })
        }) ?? [];
        //add a first element for backbone if present
        if (this.building.backboneInterface && this.currentRiserInterfaces.length === 0) {
          this.currentRiserInterfaces.push(
            new PeInterfaceConfig({
              mode: this.building.backboneInterface.mode!,
              peRiserConfigId: this.riser.riserId!,
              peBuildingConfigId: this.building.buildingId,
              numberOfApartments: 0,
              id: this.building.backboneInterface.riserInterfaceId!
            })
          );
        }
      }
      this.newApartments = 1;
    }
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  get dialogTitle() {
    return this.isEdit
      ? this.$t('AppResourceEP.EditApartment')
      : this.$t('AppResourceEP.AddApartments');
  }

  get inputLabel() {
    let label = '';
    if (this.isEdit) {
      label = `${this.$t('AppResource.NameUpper')} ${this.$t(
        'AppResourceEP.Apartment'
      )} ${this.building.apartments!.indexOf(this.apartmentToEdit) + 1}`;
    }
    return label;
  }

  get buttonSubmitLabel() {
    return this.$t('AppResource.Save');
  }

  get saveButtonDisabled() {
    if (this.isEdit) {
      return !this.isValidName || !this.apartment.name?.trim();
    } else {
      if (this.currentEntrancePlant.hasRiserInterface && !this.isBrfa) {
        const apartmentsToAdd = this.currentRiserInterfaces.reduce((acc, riserInterface) => acc + (riserInterface.numberOfApartments ?? 0), 0);
        return apartmentsToAdd < 1 || apartmentsToAdd > this.availableApartments;
      }
      else if (this.currentEntrancePlant.hasRiserInterface && this.isBrfa) {
        const totalApartments = this.floorsInterfaceConfig.reduce((acc, floorConfig) => {
          return acc + floorConfig.interfaceApartments.reduce((floorAcc, iface) => floorAcc + (iface.numberOfApartments ?? 0), 0);
        }, 0);
        return totalApartments < 1 || totalApartments > this.availableApartments;
      }
      else if (this.isBrfa) {
        // Old BRFA logic
        return !this.apartment.name?.trim();
      }
      else {
        return this.newApartments < 1 || this.newApartments > this.availableApartments;
      }
    }
  }

  get isBeenEdited() {
    return !this.isEdit || this.apartmentToEdit.name !== this.apartment.name || this.apartmentToEdit.riserInterfaceId !== this.apartment.riserInterfaceId;
  }

  get risersInterfaces(): { name: string; riserInterfaceId: string }[] {
    const result: { name: string; riserInterfaceId: string }[] = [];
    if (!this.building) return result; 
    if (this.building.backboneInterface) {
      result.push(
          { name: this.$t('AppResourceEP.Backbone'), riserInterfaceId: this.building.backboneInterface.riserInterfaceId! }
      );
    }
    
    this.riser?.interfaces?.forEach(riserInterface => {
      result.push(
        { name: this.getRiserName(this.riser, riserInterface), riserInterfaceId: riserInterface.riserInterfaceId! }
      );
    });

    return result;
  }

  getRiserName(riser: RiserDTO, riserInterface: RiserInterfaceDTO): string {
    if (riser.riserId === EMPTY_GUID) {
      return this.$t('AppResourceEP.Backbone');
    }

    if (riserInterface.mode == RiserInterfaceMode.Mode5IndipendentRiser) {
      return riser.name ?? '';
    } else {
      const modeLabel = getInterfaceModeLabel(riserInterface.mode!);
      let result = `${modeLabel}`;
      if (riserInterface.mode === RiserInterfaceMode.Mode7ExtendedRiserExpansion && riser.interfaces!.length > 2) {
        const orderValue = riser.interfaces!.indexOf(riserInterface) === 1 ? this.$t("AppResourceEP.First") : this.$t("AppResourceEP.Second");
        result += ` (${orderValue})`;
      }
      return result;
    }
  }
  

  loadRisersFloorApartment() {
    this.riserFloors = [];
    this.floorsInterfaceConfig = [];
    const floors = this.building.floors!.filter((floor) => {
      if (this.isBackboneContext) {
        return floor.riserId == null;
      }
      return floor.riserId === this.apartment.riserId;
    });
    for (const floor of floors) {
      const apartments = this.building.apartments!.filter(
        (apart) => apart.floorId === floor.floorId
      );
      this.riserFloors.push({ floor: floor, apartments: apartments });

      // Initialize interface config for this floor only when hasRiserInterface && isBrfa
      if (this.currentEntrancePlant.hasRiserInterface && this.isBrfa) {
        const interfaceApartments: Array<{
          interface: RiserInterfaceDTO;
          numberOfApartments: number;
        }> = [];
        if (this.isBackboneContext && this.building.backboneInterface) {
          interfaceApartments.push({
            interface: this.building.backboneInterface,
            numberOfApartments: 0,
          });
        } else if (this.riser.interfaces) {
          for (const iface of this.riser.interfaces) {
            interfaceApartments.push({
              interface: iface,
              numberOfApartments: 0,
            });
          }
        }
        this.floorsInterfaceConfig.push({ floor, interfaceApartments });
      }
    }
    if (
      this.riserFloors.length > 0 &&
      (
        this.apartment.floorId === undefined ||
        this.riserFloors.findIndex(
          (riser) => riser.floor.floorId === this.apartment.floorId
        ) === -1
      )
    ) {
      this.apartment.floorId = this.riserFloors[0].floor.floorId;
      if (!this.currentEntrancePlant.hasRiserInterface) {
        this.updateApartment();
      }
    }
  }

  updateApartment() {
    this.apartment.name = this.getNewApartmentName();
  }

  getNewApartmentName() {
    if (
      this.isEdit &&
      this.apartment.floorId === this.apartmentToEdit.floorId
    ) {
      return this.apartmentToEdit.name;
    } else {
      const riserIndex = this.building.risers!.findIndex(
        (riser) => riser.riserId === this.apartment.riserId
      );
      const floorIndex = this.riserFloors.findIndex(
        (riser) => riser.floor.floorId === this.apartment.floorId
      );
      return `${toLabel(
        this.currentEntrancePlant.buildings!.indexOf(this.building) + 1,
        this.currentEntrancePlant.denominationBuildings,
        this.currentEntrancePlant.brfaBlockDigits
      )}${toLabel(
        riserIndex + 1,
        this.currentEntrancePlant.denominationRisers,
        this.currentEntrancePlant.brfaRiserDigits
      )}${toLabel(
        floorIndex + 1,
        this.currentEntrancePlant.denominationFloors,
        this.currentEntrancePlant.brfaFloorDigits
      )}${toLabel(
        this.riserFloors[floorIndex].apartments.length + 1,
        this.currentEntrancePlant.denominationApartments,
        this.currentEntrancePlant.brfaApartmentDigits
      )}`;
    }
  }

  verifyValidityOfName() {
    if (this.apartment.name === undefined) return;
    if (this.currentEntrancePlant.hasRiserInterface) return;

    const nameToCheck = this.apartment.name.trim();
    let listToCheck = this.building.apartments || [];

    if (this.isEdit) {
      listToCheck = listToCheck.filter(
        (value) => value.apartmentId !== this.apartmentToEdit.apartmentId
      );
    }
    const exist = listToCheck.some(
      (existing) =>
        existing.name?.toLowerCase().trim() == nameToCheck.toLowerCase().trim()
    );
    this.isValidName = !exist;
    this.invalidNameReason = this.isValidName
      ? ''
      : this.$t('general.alreadyExisting');
  }

  async handleButtonSubmitAction() {
    this.isDialogVisible = false;
    if (this.isBeenEdited) {
      await this.saveApartment();
    }
    this.$emit('update-list');
  }

  async saveApartment() {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      if (this.isEdit) {
        await plantsClient.apartmentPUT(
          this.currentEntrancePlant.desktopId,
          this.apartment
        );
      } else {
        if (this.currentEntrancePlant.hasRiserInterface) 
          await this.saveNewApartmentsWithInterfaces();
        else
          await this.saveNewApartments();
      }
      this.$toast.add({
        severity: 'success',
        summary: this.isEdit
          ? this.$t('entrancePlant.editedApartment')
          : this.$t('entrancePlant.apartmentsBannerCreated'),
        closable: false,
        life: 3000,
      });
    } catch (error) {
      console.log(error);
      setTimeout(() => {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          closable: false,
          life: 3000,
        });
      }, 1500);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  async saveNewApartmentsWithInterfaces() {
    if (this.currentEntrancePlant.desktopId === undefined) return;

    if (this.isBrfa) {
      for (const floorConfig of this.floorsInterfaceConfig) {
        for (const interfaceAp of floorConfig.interfaceApartments) {
          for (let i = 0; i < (interfaceAp.numberOfApartments ?? 0); i++) {
            const newApartment = new ApartmentDTO({
              buildingId: this.building.buildingId,
              riserId: this.apartment.riserId,
              floorId: floorConfig.floor.floorId,
              riserInterfaceId: interfaceAp.interface.riserInterfaceId,
            });
            // Temporarily set floor to generate correct name
            // this.apartment.floorId = floorConfig.floor.floorId;
            // newApartment.name = this.getNewApartmentName();
            await plantsClient.apartmentbrfa(
              this.currentEntrancePlant.desktopId,
              newApartment
            );
          }
        }
      }
    } else {
      const updatedStructure = new EntrancePlantStructureCreateDTO();
      updatedStructure.pePlantConfig = new PePlantConfigDTO({
        pePlantConfig: new PePlantConfig({
          interfaces: this.currentRiserInterfaces
        })
      });
      await plantsClient.apartmentAll(
        this.currentEntrancePlant.desktopId,
        updatedStructure);
    }
  }

  async saveNewApartments() {
    if (this.currentEntrancePlant.desktopId === undefined) return;

    if (this.isBrfa) {
      await plantsClient.apartmentbrfa(
        this.currentEntrancePlant.desktopId,
        this.apartment
      )
    } else {
      const updatedStructure = new EntrancePlantStructureCreateDTO();
      const newBuildingsApartments = new BuildingCreateDTO({
        buildingId: this.building.buildingId,
        apartmentsCount: this.newApartments,
      });
      updatedStructure.buildingsCreate = [newBuildingsApartments];

      await plantsClient.apartmentAll(
        this.currentEntrancePlant.desktopId,
        updatedStructure
      );
    }
  }

  getInterfaceDescription(mode: RiserInterfaceMode | undefined, index: number, totInterfaces: number) {
    if (!mode && totInterfaces == 1) return this.$t('AppResourceEP.Backbone');
    if (mode === RiserInterfaceMode.Mode5IndipendentRiser || 
        mode === RiserInterfaceMode.Mode6ExtendedRiser || 
        (mode === RiserInterfaceMode.Mode7ExtendedRiserExpansion && totInterfaces === 2)) {
      return `${this.$t("AppResourceEP.RiserInterface")} ${mode}`;
    }

    if (mode === RiserInterfaceMode.Mode7ExtendedRiserExpansion && totInterfaces > 2) {
      const orderValue = index === 1 ? this.$t("AppResourceEP.First") : this.$t("AppResourceEP.Second");
      return `${this.$t("AppResourceEP.RiserInterface")} ${mode} (${orderValue})`;
    }
  }

}
